import { useMutation } from "@tanstack/react-query";
import {
  ApiError,
  createCoachingClip,
  updateCoachingClip,
} from "@/lib/api";
import { isCurrentDraftContext, type DraftContext } from "../composerDrafts";
import { invalidateScope, queryClient } from "@/lib/queryClient";
import type {
  CoachingClip,
  CreateCoachingClipRequest,
  UpdateCoachingClipRequest,
} from "@/lib/types";

/**
 * Save (create OR update) a coaching clip.
 *
 * Discriminated input, mirroring `useSaveCoachingNote`: `mode:
 * "create"` POSTs, `mode: "update"` PATCHes. The split matters
 * because the backend's `UpdateCoachingClipRequest` is
 * `extra="forbid"` and OMITS `match_id` / `slot` — a clip's match
 * anchor is immutable after creation (rebinding it would silently
 * swap the captured drawing snapshot's frame of reference). The
 * caller builds the right shape per mode; the clip composer
 * disables the match/slot inputs in edit mode so the forbidden
 * fields never get into the PATCH body.
 *
 * `source_note_id` is CREATE-only — never re-emitted on PATCH (the
 * Update request type doesn't even have the field). Rebinding the
 * source mid-life would swap which note's drawing the clip
 * snapshotted.
 *
 * Cache invalidation: `invalidateScope()` on success — the
 * coach-review bundle (which carries the seed clip in edit mode)
 * and any open clip lists refetch. Not optimistic; the M5 surface
 * isn't tracking the clip list in a way that justifies it (clip
 * list pages are M6).
 */

type SaveClipInput =
  | { mode: "create"; body: CreateCoachingClipRequest }
  | { mode: "update"; id: number; body: UpdateCoachingClipRequest };

export function useSaveCoachingClip(context: DraftContext | null) {
  return useMutation<{ clip: CoachingClip }, ApiError, SaveClipInput>({
    meta: { composerContext: context },
    mutationFn: (input) => {
      // TanStack can yield between mutateAsync and mutationFn. Recheck here,
      // immediately before apiFetch reads credentials, and pin the owned scope.
      if (!context || !isCurrentDraftContext(context)) {
        throw new ApiError(409, "Composer context changed. Reopen your feedback before saving.");
      }
      const scope = { teamId: context.teamId, seasonId: context.seasonId };
      return input.mode === "create"
        ? createCoachingClip(input.body, scope)
        : updateCoachingClip(input.id, input.body, scope);
    },
    onSuccess: async (response, input) => {
      if (!isCurrentDraftContext(context)) return response;
      await invalidateScope();
      // Surgical bundle invalidation — the edit-mode seed clip
      // lives there; `invalidateScope`'s predicate can miss a query
      // queued before the mutation started.
      if (input.mode === "update") {
        queryClient.invalidateQueries({
          queryKey: ["coach-review-bundle"],
        });
      }
      return response;
    },
  });
}
